import clsx from "clsx";
import { useFormik } from "formik";
import React from "react";
import { Col, Row } from "react-bootstrap";
import * as Yup from "yup";
import Dropdown from "../../../components/Dropdown";
import { Input } from "../../../components/Input";
import LeftSide from "../../../components/LeftSide";
import ModalAction from "../../../components/ModalAction";
import ModalSkeleton from "../../../components/ModalSkeleton";
import StartAndEndTime from "../../../components/StartAndEndTime";
import { CalendarIcon, ReceivePaymentIcon } from "../../../constant";
import classes from "./EditCourtManagement.module.css";
// Initial values for the form
const initialValues = {
  timeSlotId: null,
  courtId: null,
  courtSportType: null,
  courtSlotDuration: "",
  courtStartTime: null,
  courtEndTime: null,
  courtCharges: null,
};

// Court status options
const courtStatusOptions = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 2 },
];

// Sport type options
const sportTypeOptions = [
  { label: "Badminton", value: "badminton" },
  { label: "Tennis", value: "tennis" },
  { label: "Soccer", value: "soccer" },
  { label: "Basketball", value: "basketball" },
];

// Days options
const daysOptions = [
  { label: "Sunday", value: 0 },
  { label: "Monday", value: 1 },
  { label: "Tuesday", value: 2 },
  { label: "Wednesday", value: 3 },
  { label: "Thursday", value: 4 },
  { label: "Friday", value: 5 },
  { label: "Saturday", value: 6 },
];

// Validation schema
const validationSchema = Yup.object().shape({
  courtSportType: Yup.string().required("Sport type is required"),
  courtSlotDuration: Yup.number()
    .required("Slot duration is required")
    .min(15, "Minimum slot duration is 15 minutes")
    .max(180, "Maximum slot duration is 180 minutes"),

  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string()
    .required("End time is required")
    .test(
      "is-after-start",
      "End time must be after start time",
      function (endTime) {
        const { startTime } = this.parent;
        if (!startTime || !endTime) return true;
        return (
          new Date(`2000-01-01T${endTime}`) >
          new Date(`2000-01-01T${startTime}`)
        );
      }
    ),
  delete: Yup.boolean(),
});

const EditCourtManagementModal = ({
  show = true,
  setShow,
  onSubmit,
  isApiCall = false,
  courtData,
}) => {
  // Initialize Formik
  const formik = useFormik({
    initialValues: courtData || initialValues,
    validationSchema,
    enableReinitialize: true, // Allow form to reinitialize when courtData changes
    onSubmit: (values) => {
      if (typeof onSubmit === "function") {
        onSubmit(values);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    resetForm,
  } = formik;

  // Helper to set field value and mark as touched
  const handleChangeWithTouch = (field) => (value) => {
    setFieldValue(field, value);
    setFieldTouched(field, true, false);
  };

  // Handle time changes from StartAndEndTime component
  const handleTimeChange = (timeType, value) => {
    const field = timeType === "start" ? "startTime" : "endTime";
    handleChangeWithTouch(field)(value);
  };

  // Handle close with reset
  const handleClose = () => {
    resetForm();
    if (typeof setShow === "function") {
      setShow(false);
    }
  };

  return (
    <ModalSkeleton
      width="700px"
      show={show}
      setShow={setShow}
      title={"Edit Court Management"}
    >
      <form onSubmit={handleSubmit}>
        <Row className={clsx(classes.controlsGrid, "mb-3")}>
          <Col xs={12} md={6}>
            <Dropdown
              isOptional={true}
              icon={<CalendarIcon className={classes.icon} />}
              label="Select Day"
              options={daysOptions}
              placeholder="Select Day"
              value={values.day}
              onChange={handleChangeWithTouch("day")}
              errorText={touched.day && errors.day}
            />
          </Col>

          <Col xs={12} md={6}>
            <Dropdown
              isOptional={true}
              label="Time Slots"
              options={sportTypeOptions}
              placeholder="Select Slots"
              value={values?.timeSlotId}
              onChange={handleChangeWithTouch("timeSlotId")}
              errorText={touched.timeSlotId && errors.timeSlotId}
            />
          </Col>

          <Col xs={12}>
            <StartAndEndTime
              disabled={true}
              startLabel="Slot Start Time"
              endLabel="Slot End Time"
              isOptional={true}
              startTime={values.courtStartTime}
              endTime={values.courtEndTime}
              onChange={handleTimeChange}
              errors={{
                startTime: touched.courtStartTime && errors.courtStartTime,
                endTime: touched.courtEndTime && errors.courtEndTime,
              }}
            />
          </Col>

          <Col xs={12}>
            <Input
              leftIcon={
                <LeftSide
                  disabled={true}
                  icon={
                    <ReceivePaymentIcon
                      className={clsx(classes.leftIcon, classes.iconSize)}
                    />
                  }
                  right={"Rs."}
                />
              }
              inputStyle={{ paddingLeft: "0" }}
              labelStyle={{ fontSize: "16px" }}
              label="Price"
              type="number"
              placeholder="Enter price"
              value={values.price}
              onChange={handleChangeWithTouch("price")}
              errorText={touched.price && errors.price}
            />
          </Col>
        </Row>

        <ModalAction
          isApiCall={isApiCall}
          onCancel={handleClose}
          type="submit"
        />
      </form>
    </ModalSkeleton>
  );
};

export default EditCourtManagementModal;
